import React, { useEffect, useMemo } from "react";
import {
  useBeforeUnload,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import panelStyles from "../styles/Panel.module.css";
import pageStyles from "../styles/PageLayout.module.css";
import PageHeader from "../shared/PageHeader";
import PageControls from "../shared/PageControls";
import PageContent from "../shared/PageContent";
import TriggersSection from "../sections/TriggersSection";

export function TriggersPage({ triggers }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeTriggerId = "" } = useParams();
  const triggerList = useMemo(
    () => (Array.isArray(triggers.state.config?.triggers) ? triggers.state.config.triggers : []),
    [triggers.state.config?.triggers]
  );

  const routeTrigger = useMemo(
    () => triggerList.find((item) => item.id === routeTriggerId) || null,
    [routeTriggerId, triggerList]
  );

  const missingRouteTrigger = Boolean(routeTriggerId && !routeTrigger && triggerList.length > 0 && !triggers.state.loading);
  const dirty = Boolean(triggers.state.dirty);

  useBeforeUnload(
    React.useCallback(
      (event) => {
        if (!dirty) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [dirty]
    )
  );

  useEffect(() => {
    if (!dirty) return undefined;

    const handleDocumentClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest("a[href]");
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href || !href.startsWith("/")) return;
      const currentPath = `${location.pathname}${location.search}`;
      if (href === currentPath) return;

      const shouldLeave = window.confirm("Есть несохраненные изменения в триггерах. Перейти на другую страницу?");
      if (!shouldLeave) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [dirty, location.pathname, location.search]);

  useEffect(() => {
    if (triggers.state.loading || triggerList.length === 0) return;
    if (!routeTriggerId) {
      const firstId = triggerList[0]?.id;
      if (firstId) {
        triggers.actions.setSelectedTriggerId(firstId);
        navigate(`/admin/triggers/${firstId}`, { replace: true });
      }
      return;
    }

    if (routeTrigger) {
      triggers.actions.setSelectedTriggerId(routeTrigger.id);
    }
  }, [
    navigate,
    routeTrigger,
    routeTriggerId,
    triggerList,
    triggers.actions,
    triggers.state.loading
  ]);

  const handleSelectTrigger = (triggerId) => {
    triggers.actions.setSelectedTriggerId(triggerId);
    navigate(`/admin/triggers/${triggerId}`);
  };

  const handleCreateTrigger = (stage) => {
    const nextId = triggers.actions.addTrigger(stage);
    if (nextId) {
      navigate(`/admin/triggers/${nextId}`);
    }
  };

  const handleDuplicateTrigger = (triggerId) => {
    const nextId = triggers.actions.duplicateTrigger(triggerId);
    if (nextId) {
      navigate(`/admin/triggers/${nextId}`);
    }
  };

  const handleRemoveTrigger = (triggerId) => {
    const nextId = triggers.actions.removeTrigger(triggerId);
    if (nextId) {
      navigate(`/admin/triggers/${nextId}`, { replace: true });
    } else {
      navigate("/admin/triggers", { replace: true });
    }
  };

  return (
    <div className={pageStyles.page}>
      <PageHeader
        title="Триггеры"
        description="Управляйте автоматизациями жизненного цикла, бонусами, таймингами и правилами доставки."
        actions={
          <>
            <button
              type="button"
              className={panelStyles.buttonSecondary}
              onClick={triggers.actions.reload}
              disabled={triggers.state.loading || triggers.state.saving}
            >
              {triggers.state.loading ? "Загрузка..." : "Перезагрузить"}
            </button>
            <button
              type="button"
              className={panelStyles.buttonSecondary}
              onClick={triggers.actions.resetToDefaults}
              disabled={triggers.state.loading || triggers.state.saving}
            >
              Сбросить по умолчанию
            </button>
            <button
              type="button"
              className={panelStyles.button}
              onClick={triggers.actions.save}
              disabled={triggers.state.saving || !dirty}
            >
              {triggers.state.saving ? "Сохраняем..." : "Сохранить изменения"}
            </button>
          </>
        }
      />

      <PageControls>
        <div className={panelStyles.chips}>
          <span className={panelStyles.chip}>Версия конфига: {triggers.state.config?.version || 1}</span>
          <span className={panelStyles.chip}>
            Текущий route: {routeTriggerId || "авто"}
          </span>
          {dirty ? <span className={panelStyles.chip}>Есть несохраненные изменения</span> : null}
        </div>
      </PageControls>

      <PageContent>
        <TriggersSection
          mode="page"
          state={triggers.state}
          actions={triggers.actions}
          selectedTriggerId={routeTrigger?.id || ""}
          selectedTrigger={routeTrigger}
          missingRouteTrigger={missingRouteTrigger}
          onSelectTrigger={handleSelectTrigger}
          onCreateTrigger={handleCreateTrigger}
          onDuplicateTrigger={handleDuplicateTrigger}
          onRemoveTrigger={handleRemoveTrigger}
        />
      </PageContent>
    </div>
  );
}

export default TriggersPage;
